import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Search, 
  Filter, 
  Sprout, 
  Droplets, 
  FlaskConical, 
  Shield,
  Package,
  Leaf,
  X
} from 'lucide-react';
import { useProducts } from '../context/ProductContext';

const API = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "production" ? "https://stigmafloraweb.onrender.com/api" : "http://localhost:8000/api");
const ASSET_BASE = API.replace(/\/api$/, '');

export const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { products, loading } = useProducts();
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'All');

  const categories = [
    { name: 'All', icon: <Leaf className="w-4 h-4" /> },
    { name: 'Bio Stimulants', icon: <Sprout className="w-4 h-4" /> },
    { name: 'Micro Nutrient Fertilizers', icon: <Droplets className="w-4 h-4" /> },
    { name: 'Water Soluble Fertilizers', icon: <FlaskConical className="w-4 h-4" /> },
    { name: 'IPM Tools', icon: <Shield className="w-4 h-4" /> },
  ];

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Bio Stimulants':
        return <Sprout className="w-5 h-5" />;
      case 'Micro Nutrient Fertilizers':
        return <Droplets className="w-5 h-5" />;
      case 'Water Soluble Fertilizers':
        return <FlaskConical className="w-5 h-5" />;
      case 'IPM Tools':
        return <Shield className="w-5 h-5" />;
      default:
        return <Leaf className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Bio Stimulants':
        return 'bg-emerald-500';
      case 'Micro Nutrient Fertilizers':
        return 'bg-amber-500';
      case 'Water Soluble Fertilizers':
        return 'bg-teal-500';
      case 'IPM Tools':
        return 'bg-rose-500';
      default:
    }
  };

  useEffect(() => {
    let filtered = products;

    if (selectedCategory !== 'All') {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.description.toLowerCase().includes(term) ||
          p.benefits.toLowerCase().includes(term) ||
          (p.crops && p.crops.toLowerCase().includes(term))
      );
    }

    setFilteredProducts(filtered);
  }, [selectedCategory, searchTerm, products]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category === 'All') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSelectedCategory('All');
    setSearchTerm('');
    setSearchParams({});
  };

  return (
    <div data-testid="products-page" className="min-h-screen pt-20 bg-slate-50">
      {/* Header */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-emerald-900 to-emerald-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center">
            <span className="text-sm uppercase tracking-widest font-semibold text-emerald-400 mb-4 block">
              Our Products
            </span>
            <h1 
              data-testid="products-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] tracking-tight mb-6"
            >
              Agricultural Solutions
            </h1>
            <p className="text-lg text-emerald-200 max-w-2xl mx-auto">
              Explore our comprehensive range of bio-stimulants, fertilizers, and IPM tools 
              designed to maximize your crop yield and health.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                data-testid="product-search"
                type="text"
                placeholder="Search products, crops, benefits..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 rounded-full border-slate-200"
              />
            </div>

            {/* Category Filters */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto">
              <Filter className="w-5 h-5 text-slate-400 flex-shrink-0" />
              {categories.map((cat) => (
                <Button
                  key={cat.name}
                  data-testid={`category-filter-${cat.name.toLowerCase().replace(/ /g, '-')}`}
                  variant={selectedCategory === cat.name ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleCategoryChange(cat.name)}
                  className={`rounded-full flex items-center gap-2 whitespace-nowrap ${
                    selectedCategory === cat.name
                      ? 'bg-emerald-900 hover:bg-emerald-800 text-white'
                      : 'border-slate-200 text-slate-600 hover:bg-emerald-50 hover:text-emerald-900 hover:border-emerald-200'
                  }`}
                >
                  {cat.icon}
                  {cat.name}
                </Button>
              ))}
            </div>
          </div>

          {/* Active Filters */}
          {(selectedCategory !== 'All' || searchTerm) && (
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <span className="text-sm text-slate-500">Active filters:</span>
              {selectedCategory !== 'All' && (
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                  {selectedCategory}
                  <button onClick={() => handleCategoryChange('All')} className="ml-1">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {searchTerm && (
                <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                  "{searchTerm}"
                  <button onClick={() => setSearchTerm('')} className="ml-1">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              <button
                onClick={clearFilters}
                className="text-sm text-emerald-700 hover:text-emerald-900 underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-12 w-12 bg-slate-200 rounded-xl mb-4" />
                    <div className="h-6 bg-slate-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-1/2 mb-4" />
                    <div className="h-20 bg-slate-200 rounded mb-4" />
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Package className="w-10 h-10 text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No products found</h3>
              <p className="text-slate-600 mb-6">
                Try adjusting your search or filter criteria
              </p>
              <Button
                onClick={clearFilters}
                className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <p className="text-slate-600">
                  Showing <span className="font-semibold text-slate-900">{filteredProducts.length}</span> products
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts.map((product, index) => (
                  <Card
                    key={product.id}
                    data-testid={`product-card-${product.id}`}
                    className="border-slate-200 bg-white hover:shadow-xl transition-all duration-300 overflow-hidden group"
                  >
                    {/* Product Image */}
                    {product.image_url && (
                      <div className="relative h-64 w-full p-4 flex items-center justify-center bg-white border-b border-slate-100">
                        <img 
                          src={product.image_url.startsWith('http') ? product.image_url : `${ASSET_BASE}${product.image_url}`}
                          alt={product.name}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between mb-3">
                        <div className={`w-12 h-12 ${getCategoryColor(product.category)} rounded-xl flex items-center justify-center text-white group-hover:scale-110 transition-transform`}>
                          {getCategoryIcon(product.category)}
                        </div>
                        <Badge 
                          variant="outline" 
                          className="text-xs border-emerald-200 text-emerald-700 bg-emerald-50"
                        >
                          {product.category}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-semibold text-slate-900 font-['Outfit']">
                        {product.name}
                      </CardTitle>
                    </CardHeader>
                    
                    <CardContent className="pt-0 space-y-4">
                      <p className="text-slate-600 text-sm line-clamp-2">
                        {product.description}
                      </p>

                      <div className="bg-emerald-50 rounded-xl p-4">
                        <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-2">
                          Key Benefits
                        </p>
                        <p className="text-sm text-emerald-900 line-clamp-3">
                          {product.benefits}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        {product.crops && (
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                              Crops
                            </p>
                            <p className="text-sm text-slate-700 line-clamp-1">
                              {product.crops}
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                            Dosage
                          </p>
                          <p className="text-sm text-slate-700 line-clamp-1">
                            {product.dosage}
                          </p>
                        </div>
                      </div>

                      <div className="pt-3 border-t border-slate-100">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                          Available Packs
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {product.packs.split(',').map((pack, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-md"
                            >
                              {pack.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};
